import { DateTimeRangePicker } from '@optios/react-date-time-range-picker'
import moment, { Moment } from 'moment'
import React, { SyntheticEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import Dropdown from '../../../../App/Components/Dropdown/Dropdown'
import { useAppSelector } from '../../../../App/Hooks/reduxHooks'
import {
  IAbsenceRequest,
  IAbsenceRequestType,
} from '../../../../HR/AbsenceRequests/Client/IAbsenceRequest'

import style from './Form.scss'
import {
  ErrorMessageContainer,
  ErrorMessageIcon,
  ErrorMessageText,
} from './style'
interface IProps {
  absenceRequest?: IAbsenceRequest
  onSubmit?: (absence: IAbsenceRequest) => void
  actionType: 'add' | 'review' | 'edit'
  isDisabled?: boolean
}

export const AbsenceRequestForm = ({
  onSubmit,
  absenceRequest,
  actionType,
  isDisabled = false,
}: IProps) => {
  const { t } = useTranslation()
  const history = useHistory()
  const [isWindowSmall, setIsWindowSmall] = useState<boolean>(false)
  const [isBeforeCheck, setIsBeforeCheck] = useState<boolean>(false)

  const defaultDay: Moment = moment().minutes(
    Math.round(moment().minutes() / 15) * 15,
  )

  const defaultDayStart: Moment = defaultDay
    .clone()
    .set('hour', 8)
    .set('minute', 30)

  const defaultDayEnd = defaultDay.clone().set('hour', 18).set('minute', 0)

  const [startsAt, setStartsAt] = useState<moment.Moment>(
    absenceRequest ? absenceRequest.startsAt : defaultDayStart,
  )
  const [endsAt, setEndsAt] = useState(
    absenceRequest ? absenceRequest.endsAt : defaultDayEnd,
  )
  const [type, setType] = useState<IAbsenceRequestType>(
    absenceRequest ? absenceRequest.type : 'personal_holiday',
  )
  const [reason, setReason] = useState<string>(
    absenceRequest ? absenceRequest.reason : '',
  )
  const [status, setStatus] = useState<string>(
    absenceRequest ? absenceRequest.status : null,
  )
  const [reviewReason, setReviewReason] = useState<string>(
    absenceRequest ? absenceRequest.reviewReason : '',
  )

  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const error = useAppSelector(state => state.hr.absenceRequests)

  useEffect(() => {
    setErrorMessage(error.error)
    const timer = setTimeout(() => {
      setErrorMessage(null)
    }, 7000)

    return () => clearTimeout(timer)
  }, [error])

  const checkWindowSize = () => {
    const isSmallNow = window.innerWidth < 620
    if (!isSmallNow && isWindowSmall) {
      setIsWindowSmall(false)
    } else if (isSmallNow && !isWindowSmall) {
      setIsWindowSmall(true)
    }
  }

  useEffect(() => {
    checkWindowSize()
    window.addEventListener('resize', checkWindowSize)
  }, [])

  const absenceTypes: IAbsenceRequestType[] = [
    'personal_holiday',
    'public_holiday',
    'sick_leave',
    'other',
  ]

  const user = useAppSelector(state => state.users.currentUser)

  const getButtons = () => {
    const buttons = []

    if (
      'edit' === actionType &&
      undefined !== absenceRequest &&
      ('DECLINED' === absenceRequest.status ||
        'APPROVED' === absenceRequest.status)
    ) {
      buttons.push(null)
    } else if (actionType === 'edit' || actionType === 'add') {
      buttons.push(
        <button type={'submit'} className={'button'}>
          {t('Save')}
        </button>,
      )
    } else if (
      'review' === actionType &&
      undefined !== absenceRequest &&
      'PENDING' === absenceRequest.status &&
      true === user.permissions.hrAbsencesManage
    ) {
      buttons.push(
        <>
          <button
            type={'submit'}
            className={'button'}
            onClick={() => setStatus('APPROVED')}>
            {t('Approve')}
          </button>
          <button
            type={'submit'}
            className={'button destructive'}
            onClick={() => setStatus('DECLINED')}>
            {t('Decline')}
          </button>
        </>,
      )
    }

    buttons.push(
      <button
        type={'button'}
        className={'button dim'}
        onClick={() => history.goBack()}>
        {t('Cancel')}
      </button>,
    )

    return buttons
  }

  const getReviewedReasonField = () => {
    if (!absenceRequest) {
      return
    }

    if (
      'edit' === actionType &&
      ('DECLINED' === absenceRequest.status ||
        'APPROVED' === absenceRequest.status)
    ) {
      return (
        <label className={'full'}>
          <div className={'text'}>{t('Reason of reviewer')}</div>
          <textarea
            className={'input'}
            rows={3}
            onChange={(e: SyntheticEvent<HTMLTextAreaElement>) =>
              setReviewReason(e.currentTarget.value)
            }
            value={reviewReason}
            disabled
          />
        </label>
      )
    } else if (
      'review' === actionType &&
      'PENDING' === absenceRequest.status &&
      user &&
      true === user.permissions.hrAbsencesManage
    ) {
      return (
        <label className={'full'}>
          <div className={'text'}>{t('Reason of reviewer')}</div>
          <textarea
            className={'input'}
            rows={3}
            onChange={(e: SyntheticEvent<HTMLTextAreaElement>) =>
              setReviewReason(e.currentTarget.value)
            }
            value={reviewReason}
            disabled={false}
          />
        </label>
      )
    } else if (
      'review' === actionType &&
      ('DECLINED' === absenceRequest.status ||
        'APPROVED' === absenceRequest.status) &&
      true === user.permissions.hrAbsencesManage
    ) {
      return (
        <label className={'full'}>
          <div className={'text'}>{t('Reason of reviewer')}</div>
          <textarea
            className={'input'}
            rows={3}
            onChange={(e: SyntheticEvent<HTMLTextAreaElement>) =>
              setReviewReason(e.currentTarget.value)
            }
            value={reviewReason}
            disabled
          />
        </label>
      )
    } else {
      return null
    }
  }

  return (
    <form
      className={style.formSplit}
      onSubmit={(e: SyntheticEvent<HTMLFormElement>) => {
        e.preventDefault()

        if (onSubmit && !isBeforeCheck) {
          onSubmit({
            id: absenceRequest ? absenceRequest.id : null,
            person: {
              id: user ? user.id : null,
              uuid: user ? user.uuid : null,
              avatar: user ? user.avatar : null,
              firstName: user ? user.firstName : null,
              lastName: user ? user.lastName : null,
              fullName: user ? `${user.firstName} ${user.lastName}` : null,
            },
            startsAt,
            endsAt,
            type,
            reason,
            status,
            reviewReason,
          })
        }
      }}>
      {/* todo: change DateTimeRangePicker to be able to VISUALLY disable this*/}
      {isDisabled ? (
          <>
            {errorMessage && (
                <>
                  <ErrorMessageContainer show={true}>
                    <ErrorMessageIcon icon={'calendar-exclamation'} />
                    <ErrorMessageText>{t(`${errorMessage}`)}</ErrorMessageText>
                  </ErrorMessageContainer>
                </>
            )}
            <DateTimeRangePicker
                range
                date
                time
                months={isWindowSmall ? 1 : 2}
                fromDate={startsAt}
                untilDate={endsAt}
            />
          </>

      ) : (
        <>
          {errorMessage && (
              <>
                <ErrorMessageContainer show={true}>
                  <ErrorMessageIcon icon={'calendar-exclamation'} />
                  <ErrorMessageText>{t(`${errorMessage}`)}</ErrorMessageText>
                </ErrorMessageContainer>
              </>
          )}
          <DateTimeRangePicker
            range
            date
            time
            months={isWindowSmall ? 1 : 2}
            fromDate={startsAt}
            untilDate={endsAt}
            onChange={(newStartsAt: Moment, newEndsAt: Moment) => {
              setIsBeforeCheck(newEndsAt.isBefore(newStartsAt))
              if (newStartsAt.isSame(newEndsAt, 'day')) {
                newEndsAt = newEndsAt.clone().set({
                  hour: 18,
                  minute: 0,
                  second: 0,
                  millisecond: 0,
                })
              }
              setStartsAt(newStartsAt.clone())
              setEndsAt(newEndsAt.clone())
            }}
          />
        </>
      )}
      {isBeforeCheck ? (
        <p style={{ color: 'red' }}>
          {t("End's at can't be before start's at.")}
        </p>
      ) : (
        isBeforeCheck
      )}
      <Dropdown
        label={t('Absence type')}
        onChange={(e: SyntheticEvent<HTMLSelectElement>) =>
          setType(e.currentTarget.value as IAbsenceRequestType)
        }
        value={type}
        className={style.absenceTypeDropdown}
        isDisabled={isDisabled}>
        {absenceTypes.map((absenceType: IAbsenceRequestType) => (
          <option value={absenceType} key={`${absenceType}`}>
            {t(`absence.${absenceType}`)}
          </option>
        ))}
      </Dropdown>
      <label className={'full'}>
        <div className={'text'}>{t('Reason')}</div>
        <textarea
          className={'input'}
          rows={3}
          onChange={(e: SyntheticEvent<HTMLTextAreaElement>) =>
            setReason(e.currentTarget.value)
          }
          value={reason}
          disabled={isDisabled}
        />
      </label>
      {getReviewedReasonField()}
      {getButtons()}
    </form>
  )
}
